import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Portfolio() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [items, setItems] = useState([]);
  const swiperRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/portfolio`)
      .then(res => {
        // Handle strictly requested format: { success: true, data: [...] }
        const payload = res.data?.data || res.data;
        if (Array.isArray(payload)) setItems(payload);
      })
      .catch((error) => {
        console.error("Portfolio fetch error:", error);
        setItems([
        { id: '1', client_name: 'Kopi Nusantara', industry: 'F&B / Kopi', problem: 'Brand kopi lokal dengan kualitas premium tapi penjualan online stagnan. Website lama hanya brosur digital tanpa konversi.', discovery_why: 'Kopi Nusantara bukan sekadar menjual kopi—mereka melestarikan warisan rasa Nusantara.', solution: 'Redesign total dengan storytelling-first approach. Integrasi e-commerce dengan checkout 2 langkah.', impact_metrics: { conversion_increase: '340%', revenue: 'Rp 2.1M/bulan', bounce_reduction: '52%' }, thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' },
        { id: '2', client_name: 'TechVenture ID', industry: 'SaaS / Teknologi', problem: 'Startup SaaS dengan churn rate tinggi. Dashboard kompleks membingungkan pengguna baru.', discovery_why: 'TechVenture memberdayakan tim kecil untuk berpikir besar.', solution: 'UX audit mendalam + redesign dashboard berbasis persona.', impact_metrics: { churn_reduction: '28%', onboarding_completion: '89%', nps_increase: '+34 poin' }, thumbnail: 'https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?w=600' },
        { id: '3', client_name: 'Batik Aura', industry: 'Fashion / E-commerce', problem: 'Brand batik premium yang ingin menembus pasar internasional.', discovery_why: 'Batik Aura adalah jembatan antara warisan budaya dan gaya hidup modern global.', solution: 'Website bilingual (ID/EN) dengan storytelling visual. SEO internasional.', impact_metrics: { international_orders: '+180%', avg_order_value: '+65%', page_views: '3.2x' }, thumbnail: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600' },
      ]);
    });
  }, []);

  return (
    <section
      data-testid="portfolio-section"
      id="portofolio"
      ref={ref}
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <div className="flex items-end justify-between mb-12">
          <div>
            <motion.span
              className="font-mono text-xs tracking-[0.2em] uppercase block mb-4"
              style={{ color: 'var(--accent-primary)' }}
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
            >
              The Evidence
            </motion.span>
            <motion.h2
              className="font-heading text-3xl md:text-5xl font-semibold tracking-tight"
              style={{ color: 'var(--text-primary)' }}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
            >
              Portofolio Naratif
            </motion.h2>
          </div>
          {/* Desktop navigation arrows */}
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              data-testid="portfolio-prev"
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5"
              style={{ border: '1px solid var(--border-card)', color: 'var(--text-secondary)' }}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => swiperRef.current?.slideNext()}
              data-testid="portfolio-next"
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5"
              style={{ border: '1px solid var(--border-card)', color: 'var(--text-secondary)' }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Swiper Carousel (PRD §7: Swiper horizontal scroll) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={24}
            slidesPerView={1}
            pagination={{
              clickable: true,
              bulletClass: 'swiper-pagination-bullet portfolio-bullet',
              bulletActiveClass: 'swiper-pagination-bullet-active portfolio-bullet-active',
            }}
            breakpoints={{
              640: { slidesPerView: 1.5 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 2.5 },
            }}
            onSwiper={(swiper) => { swiperRef.current = swiper; }}
            data-testid="portfolio-swiper"
          >
            {items.map((item, i) => (
              <SwiperSlide key={item.id}>
                <div
                  className="glass-card p-8 group h-full"
                  data-testid={`portfolio-card-${i}`}
                >
                  {/* Thumbnail */}
                  <div
                    className="w-full h-48 rounded-xl mb-6 overflow-hidden"
                    style={{
                      background: theme === 'dark'
                        ? `linear-gradient(135deg, rgba(0,255,133,0.1), rgba(58,130,255,0.1))`
                        : `linear-gradient(135deg, rgba(13,148,136,0.1), rgba(37,99,235,0.1))`,
                    }}
                  >
                    {item.thumbnail && (
                      <img
                        src={item.thumbnail}
                        alt={item.client_name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    )}
                  </div>

                  <span className="font-mono text-xs tracking-wider" style={{ color: 'var(--accent-primary)' }}>
                    {item.industry}
                  </span>
                  <h3 className="font-heading text-xl font-semibold mt-1 mb-4" style={{ color: 'var(--text-primary)' }}>
                    {item.client_name}
                  </h3>

                  <div className="space-y-3 mb-6">
                    <div>
                      <span className="font-mono text-[10px] tracking-wider uppercase block mb-1" style={{ color: 'var(--text-secondary)' }}>Masalah</span>
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{item.problem}</p>
                    </div>
                    <div>
                      <span className="font-mono text-[10px] tracking-wider uppercase block mb-1" style={{ color: 'var(--text-secondary)' }}>Why</span>
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{item.discovery_why}</p>
                    </div>
                    <div>
                      <span className="font-mono text-[10px] tracking-wider uppercase block mb-1" style={{ color: 'var(--text-secondary)' }}>Solusi</span>
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{item.solution}</p>
                    </div>
                  </div>

                  {/* Impact metrics */}
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(item.impact_metrics || {}).map(([key, val]) => (
                      <span
                        key={key}
                        className="px-3 py-1.5 rounded-full font-mono text-xs"
                        style={{
                          background: theme === 'dark' ? 'rgba(0,255,133,0.1)' : 'rgba(13,148,136,0.1)',
                          color: 'var(--accent-primary)',
                        }}
                      >
                        {val}
                      </span>
                    ))}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </div>

      {/* Swiper custom bullet styles */}
      <style>{`
        .portfolio-bullet {
          width: 8px !important;
          height: 8px !important;
          border-radius: 50% !important;
          background: ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} !important;
          opacity: 1 !important;
          transition: all 0.3s !important;
          margin: 0 4px !important;
        }
        .portfolio-bullet-active {
          background: var(--accent-primary) !important;
          width: 24px !important;
          border-radius: 4px !important;
        }
      `}</style>
    </section>
  );
}
